import { PARTNER_COCKPIT_MIN_PASSWORD_LENGTH } from '@houzy/schemas/partner-cockpit/user-management';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PasswordField } from '../../../components/PasswordField';
import { credentialErrorMessageKey } from '../../../lib/api';
import { useChangePasswordMutation, useSetInitialPasswordMutation, type PartnerCockpitUser } from '../../../lib/auth';
import { SettingsCard } from './SettingsCard';

export type PasswordCardProps = {
    user: Pick<PartnerCockpitUser, 'hasPassword'>;
};

const EMPTY_FORM = { currentPassword: '', newPassword: '', confirmPassword: '' };

export function PasswordCard({ user }: PasswordCardProps): ReactElement {
    const { t } = useTranslation();
    const changePassword = useChangePasswordMutation();
    const setInitialPassword = useSetInitialPasswordMutation();
    const [form, setForm] = useState(EMPTY_FORM);
    const [validationError, setValidationError] = useState<string | undefined>(undefined);
    // Held as a key rather than read off the mutation: setting the first password refetches the user, which flips
    // `hasPassword` and swaps `mutation` for the other hook, and the confirmation would disappear with it.
    const [successKey, setSuccessKey] = useState<string | undefined>(undefined);

    const { hasPassword } = user;
    const mutation = hasPassword ? changePassword : setInitialPassword;

    const update = <K extends keyof typeof form>(key: K, next: (typeof form)[K]): void => {
        setForm((prev) => ({ ...prev, [key]: next }));
        setValidationError(undefined);
        setSuccessKey(undefined);
    };

    const isDirty =
        (!hasPassword || form.currentPassword.length > 0) &&
        form.newPassword.length > 0 &&
        form.confirmPassword.length > 0;

    const handleSave = (): void => {
        if (form.newPassword.length < PARTNER_COCKPIT_MIN_PASSWORD_LENGTH) {
            setValidationError(t('SETTINGS.ACCOUNT.PASSWORD_TOO_SHORT', { min: PARTNER_COCKPIT_MIN_PASSWORD_LENGTH }));
            return;
        }
        if (form.newPassword !== form.confirmPassword) {
            setValidationError(t('SETTINGS.ACCOUNT.PASSWORD_MISMATCH'));
            return;
        }
        const onSuccess = (): void => {
            setForm(EMPTY_FORM);
            setSuccessKey(
                hasPassword ? 'SETTINGS.ACCOUNT.PASSWORD_CHANGE_SUCCESS' : 'SETTINGS.ACCOUNT.PASSWORD_SET_SUCCESS',
            );
        };
        if (hasPassword) {
            changePassword.mutate({ oldPassword: form.currentPassword, newPassword: form.newPassword }, { onSuccess });
            return;
        }
        setInitialPassword.mutate({ password: form.newPassword }, { onSuccess });
    };

    const handleReset = (): void => {
        setForm(EMPTY_FORM);
        setValidationError(undefined);
        setSuccessKey(undefined);
    };

    const confirmMismatch = form.confirmPassword.length > 0 && form.newPassword !== form.confirmPassword;

    return (
        <SettingsCard
            title={t(hasPassword ? 'SETTINGS.ACCOUNT.PASSWORD_TITLE' : 'SETTINGS.ACCOUNT.PASSWORD_SET_TITLE')}
            dirty={isDirty}
            saving={mutation.isPending}
            onSave={handleSave}
            onReset={handleReset}>
            <Stack spacing={3}>
                {hasPassword ? undefined : <Alert severity="info">{t('SETTINGS.ACCOUNT.PASSWORD_SET_HINT')}</Alert>}
                {hasPassword ? (
                    <PasswordField
                        fullWidth
                        label={t('SETTINGS.ACCOUNT.CURRENT_PASSWORD')}
                        autoComplete="current-password"
                        value={form.currentPassword}
                        onChange={(e) => update('currentPassword', e.target.value)}
                    />
                ) : undefined}
                <PasswordField
                    fullWidth
                    label={t(hasPassword ? 'SETTINGS.ACCOUNT.NEW_PASSWORD' : 'SETTINGS.ACCOUNT.PASSWORD')}
                    autoComplete="new-password"
                    value={form.newPassword}
                    onChange={(e) => update('newPassword', e.target.value)}
                />
                <PasswordField
                    fullWidth
                    label={t(
                        hasPassword ? 'SETTINGS.ACCOUNT.CONFIRM_PASSWORD' : 'SETTINGS.ACCOUNT.CONFIRM_PASSWORD_SET',
                    )}
                    autoComplete="new-password"
                    value={form.confirmPassword}
                    onChange={(e) => update('confirmPassword', e.target.value)}
                    error={confirmMismatch}
                    helperText={confirmMismatch ? t('SETTINGS.ACCOUNT.PASSWORD_MISMATCH') : undefined}
                />
                {validationError ? <Alert severity="error">{validationError}</Alert> : undefined}
                {successKey ? <Alert severity="success">{t(successKey)}</Alert> : undefined}
                {mutation.isError ? (
                    <Alert severity="error">
                        {t(
                            credentialErrorMessageKey(
                                mutation.error,
                                hasPassword
                                    ? 'SETTINGS.ACCOUNT.PASSWORD_CHANGE_ERROR'
                                    : 'SETTINGS.ACCOUNT.PASSWORD_SET_ERROR',
                            ),
                        )}
                    </Alert>
                ) : undefined}
            </Stack>
        </SettingsCard>
    );
}
